import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { ZodError } from 'zod';
import { logEvent } from 'firebase/analytics';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { Button } from '../../commons';
import { Textarea } from '../../inputs';
import { firestore, loadAnalytics } from '../../../lib';
import { Application, Essay, essaySchema } from '../../../models';

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

interface EssayFormProps {
  application: Application;
  isReadOnly?: boolean;
}

export default function EssayForm({
  application = null,
  isReadOnly = false,
}: EssayFormProps) {
  const router = useRouter();
  const [reason, setReason] = React.useState(application?.essay?.reason ?? '');
  const [financial, setFinancial] = React.useState(
    application?.essay?.financial ?? ''
  );
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let essay: Partial<Essay> | null = null;
    try {
      essay = essaySchema.parse({ reason, financial });
    } catch (err) {
      if (err instanceof ZodError) {
        if (err instanceof ZodError) {
          return toastError(err.issues[0].message);
        }
        return toastError(
          'An error has occurred, please try again later. CODE: E61'
        );
      }
    }
    NProgress.start();
    try {
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '6',
        essay: {
          ...essay,
        },
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '6',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '6',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/7'
      );
    } catch (err) {
      posthog?.captureException(err);
      Sentry.addBreadcrumb({ level: 'error', category: 'step-6' });
      Sentry.captureException(err);
      toastError('An error has occurred, please try again later. CODE: E62');
    } finally {
      NProgress.done();
    }
  };

  const onChange =
    (setState: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      e.preventDefault();
      const MAX_WORDS = 500;
      const words = e.currentTarget.value.split(' ');
      if (words.length <= MAX_WORDS) {
        setState(words.join(' '));
      } else {
        toastError(
          `You have exceeded the maximum word count. By ${
            words.length - MAX_WORDS
          } words`
        );
      }
    };

  const onChangeReason = onChange(setReason);
  const onChangeFinancial = onChange(setFinancial);

  return (
    <form className="mt-8" method="POST" onSubmit={onSubmit}>
      <p className="mb-2">
        1. This essay should be 500 words. Kindly explain in detail your reason
        for applying for a Carreras scholarship, your community and
        extra-curricular involvements as well as describe the personal,
        intellectual, and interpersonal qualities that make you a future leader.
      </p>
      <Textarea
        label=""
        name="reason"
        rows={5}
        value={reason}
        onChange={onChangeReason}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <p className="my-2 text-gray-500 text-sm text-right">
        {reason.split(' ').length - 1} / 500 words
      </p>
      <p className="mt-8 mb-2">
        2. This essay should be 500 words. This is a needs-based scholarship so
        kindly detail the financial constraints to your academic pursuits which
        make you suitably qualified for a Carreras Scholarship. Additionally,
        briefly describe your short-term and long-term career plans, indicating
        how your course of study will help achieve those plans and benefit
        Jamaica.
      </p>
      <Textarea
        label=""
        name="financial"
        rows={5}
        value={financial}
        onChange={onChangeFinancial}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      <p className="my-2 text-gray-500 text-sm text-right">
        {financial.split(' ').length - 1} / 500 words
      </p>
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
